import React, { useEffect, useMemo, useState } from 'react';
import { AlertBanner, Button, SparkModalPortal, Spinner, Text, sparkToast } from '@spark';
import { useCommercialModels } from '@/shared/hooks/useCommercialModels';
import { useTeacherPromptAutoSave } from '@/features/model-setup/hooks/useTeacherPromptAutoSave';
import { startAIGenerationDataset } from '@/features/datasets/utils/aiGenerationDataset';
import { getUserFacingDatasetName } from '@/features/datasets/utils/datasetCloneNames';
import GenerateDataWithAIModal from '@/features/datasets/components/DataCollection/GenerateDataWithAI/GenerateDataWithAIModal';
import ModelSelection from '@/features/model-setup/components/ModelSelection';
import PromptInput from '@/features/model-setup/components/PromptInput';
import SystemPromptInput from '@/features/model-setup/components/SystemPromptInput';

const DEFAULT_PROVIDER = 'openai';

export default function TaskPromptSetupModal({
    open,
    onClose,
    usecase,
    role,
    onSaved,
    propagationMode,
}) {
    const { availableProviders } = useCommercialModels();
    const bedrockCatalog = availableProviders?.bedrock?.models || [];

    const [teacherConfig, setTeacherConfig] = useState({
        provider: usecase?.teacher_provider || DEFAULT_PROVIDER,
        model: usecase?.teacher_model || null,
    });
    const [prompt, setPrompt] = useState(usecase?.prompt || '');
    const [systemPrompt, setSystemPrompt] = useState(usecase?.teacher_system_prompt || '');
    const [saving, setSaving] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [generateWithAiOpen, setGenerateWithAiOpen] = useState(false);

    // Re-hydrate local state every time the modal opens or the active
    // subtask changes, so reopening on a different sibling doesn't show
    // the previous one's prompt/model.
    useEffect(() => {
        if (!open) return;
        setPrompt(usecase?.prompt || '');
        setSystemPrompt(usecase?.teacher_system_prompt || '');
        setTeacherConfig({
            provider: usecase?.teacher_provider || DEFAULT_PROVIDER,
            model: usecase?.teacher_model || null,
        });
        setValidationError('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, usecase?._id]);

    const isViewer = useMemo(() => role === 'Viewer', [role]);

    const {
        autosaveError,
        isDirty,
        canSave,
        flushSave,
        saveTeacherSelectionNow,
        clearAutosaveError,
    } = useTeacherPromptAutoSave({
        usecase,
        prompt,
        systemPrompt,
        teacherConfig,
        bedrockCatalog,
        enabled: open,
        propagationMode,
        role,
        onSaved,
    });

    const handleDone = async () => {
        if (isViewer) return;
        if (!usecase?._id || !usecase?.client_id) {
            sparkToast.error('Task not loaded yet');
            return;
        }
        if (!canSave) {
            setValidationError('Please enter a prompt.');
            return;
        }
        setValidationError('');

        // Flush any pending debounced saves so Done never races the
        // autosave timers (no-op when already in sync).
        try {
            setSaving(true);
            clearAutosaveError();
            await flushSave();
            setSaving(false);
            onClose?.({ reason: 'saved' });
        } catch (e) {
            setSaving(false);
            const errMessage = e?.message || 'Failed to save model & prompt';
            sparkToast.error(errMessage);
        }
    };

    const renderSaveStatus = () => {
        if (!isDirty && canSave && !autosaveError) {
            return <Text variant="base-regular" color="secondary">Saved</Text>;
        }
        return null;
    };

    const combinedAutosaveError = autosaveError;

    return (
        <>
            <SparkModalPortal
                open={open}
                onClose={() => onClose?.({ reason: 'cancel' })}
                size="text"
                destroyOnClose={false}
                style={{ zIndex: 2000 }}
            >
                <div
                    style={{
                        background: '#fff',
                        borderRadius: 12,
                        padding: 24,
                        maxHeight: 'calc(100vh - 48px)',
                        overflowY: 'auto',
                        boxSizing: 'border-box',
                    }}
                >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18, fontWeight: 600 }}>
                        Model and Prompt Setup
                    </span>
                    {renderSaveStatus()}
                </div>
                <div style={{ paddingTop: 8 }}>

                    {combinedAutosaveError && (
                        <div style={{ marginBottom: 12 }}>
                            <AlertBanner
                                feedback="error"
                                showIcon
                                showButton={false}
                                showCloseButton={false}
                                text={
                                    <span>
                                        <b>Could not save model &amp; prompt</b>
                                        <br />
                                        {combinedAutosaveError}
                                    </span>
                                }
                            />
                        </div>
                    )}

                    <ModelSelection
                        labelText="Select commercial model"
                        clientId={usecase?.client_id}
                        value={teacherConfig}
                        onChange={(next) => {
                            setTeacherConfig(next);
                            if (next?.model) {
                                void saveTeacherSelectionNow(next);
                            }
                            if (validationError) setValidationError('');
                        }}
                        role={role}
                        required={false}
                        taskType={usecase?.task_type}
                    />

                    <SystemPromptInput
                        value={systemPrompt}
                        onChange={(e) => setSystemPrompt(e.target.value)}
                        role={role}
                    />

                    <div style={{ marginTop: 24 }}>
                        <PromptInput
                            labelText="Prompt"
                            prompt={prompt}
                            onPromptChange={(e) => {
                                setPrompt(e.target.value);
                                if (validationError) setValidationError('');
                            }}
                            role={role}
                            showHelpLink={false}
                        />
                    </div>
                </div>
                {/* Footer (formerly the antd Modal footer prop). */}
                <div style={{ width: '100%', marginTop: 16 }}>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: 8,
                        }}
                    >
                        {validationError && (
                            <div
                                className="fs-unmask"
                                style={{
                                    color: '#ff4d4f',
                                    width: '100%',
                                    textAlign: 'right',
                                }}
                            >
                                {validationError}
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Button variant="secondary" onClick={() => onClose?.({ reason: 'cancel' })}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleDone}
                                disabled={isViewer || saving}
                                disableWhenReadOnly
                                startIcon={saving ? <Spinner size="small" /> : undefined}
                            >
                                Done
                            </Button>
                        </div>
                    </div>
                </div>
                </div>
            </SparkModalPortal>

            <GenerateDataWithAIModal
                visible={generateWithAiOpen}
                onCancel={() => setGenerateWithAiOpen(false)}
                usecase={usecase}
                onGenerate={(payload) => {
                    return startAIGenerationDataset({ usecase, payload })
                        .then((res) => {
                            sparkToast.success(
                                `AI generation started. Dataset: ${getUserFacingDatasetName(res?.dataset_name) || 'queued'}`
                            );
                            return res;
                        })
                        .catch((err) => {
                            sparkToast.error(
                                err?.message || 'Failed to start AI generation'
                            );
                            return Promise.reject(err);
                        });
                }}
            />
        </>
    );
}
